/**
 * The player-selectable control schemes. One setting (`controlScheme`) picks
 * among them; everything a scheme changes is resolved from this table, so the
 * controller, cameras, HUD and settings UI never compare scheme names
 * themselves beyond asking it a question.
 *
 * - `mouse` — what the mouse does: `aim` (look-to-steer: the view is the
 *   autopilot's target), `stick` (a virtual stick that stays where it's left),
 *   or `look` (a free head, relative to the cockpit).
 * - `device` — the Gamepad API device the scheme flies with, if any. Schemes
 *   with a device don't need pointer lock to fly.
 *
 * Only Basic is look-to-steer; every other scheme flies through FlightAssist
 * and gets the camera locked to the airframe's roll (see `bodyLook`).
 */
export const ControlScheme = Object.freeze({
  BASIC: 'basic',
  KEYBOARD: 'keyboard',
  MOUSE: 'mouse',
  GAMEPAD: 'gamepad',
  JOYSTICK: 'joystick',
});

export const SCHEME_INFO = Object.freeze({
  basic: {
    label: 'Basic',
    name: 'Basic (Mouse + Keyboard)',
    mouse: 'aim',
    device: null,
    blurb: 'The jet flies where you look — move the mouse and the autopilot banks into the turn. '
      + 'WASD and Q/E take over pitch, roll and yaw while held.',
  },
  keyboard: {
    label: 'Keyboard',
    name: 'Keyboard + Free Look',
    mouse: 'look',
    device: null,
    blurb: 'Fly with WASD and Q/E through the flight assist. The mouse is your head — look around freely '
      + 'without touching the controls.',
  },
  mouse: {
    label: 'Mouse',
    name: 'Manual (Mouse + Keyboard)',
    mouse: 'stick',
    device: null,
    blurb: 'The mouse is a control stick that stays where you leave it. WASD and Q/E add to it. '
      + 'Hold Space to look around with the mouse.',
  },
  gamepad: {
    label: 'Gamepad',
    name: 'Manual (Gamepad)',
    mouse: 'look',
    device: 'gamepad',
    blurb: 'An Xbox-style controller: left stick flies, right stick looks, bumpers are rudder, RT fires.',
  },
  joystick: {
    label: 'Joystick',
    name: 'Manual (Joystick)',
    mouse: 'look',
    device: 'joystick',
    blurb: 'A flight stick such as the Logitech Extreme 3D Pro: stick flies, twist is rudder, slider is throttle, '
      + 'hat looks around.',
  },
});

export const SCHEME_ORDER = Object.freeze(Object.keys(SCHEME_INFO));

/** True when the scheme's camera is locked to the airframe rather than to the horizon. */
export function isBodyLook(scheme) {
  return (SCHEME_INFO[scheme] || SCHEME_INFO.basic).mouse !== 'aim';
}
